"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useOutsideClick } from "@/hooks/use-outside-click";
import { Badge } from "./ui/badge";

export function ExpandableCard({
  title,
  location,
  department,
  type,
  qualifications,
  responsibilities,
  benefits,
  description,
  salary,
  workingHours,
  ctaText,
  ctaLink }: {
    title: string,
    location: string,
    department: string,
    type: string,
    qualifications: string,
    responsibilities: string,
    benefits: string,
    description: string,
    salary: string,
    workingHours: string,
    ctaText: string,
    ctaLink: string
  }) {
  const [active, setActive] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setActive(false);
      }
    }

    if (active) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active]);

  useOutsideClick(ref, () => setActive(false));

  return (
    <>
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-secondary/50 h-full w-full z-10"
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {active ? (
          <div className="fixed inset-0 grid place-items-center z-[100]">
            <motion.button
              key={`button-${title}-${id}`}
              layout
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
                transition: {
                  duration: 0.05,
                },
              }}
              className="flex absolute top-6 right-6 z-[110] items-center justify-center bg-neutral-100 dark:bg-neutral-800 rounded-full h-12 w-12 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors shadow-lg"
              onClick={() => setActive(false)}
            >
              <CloseIcon />
            </motion.button>
            <motion.div
              layoutId={`card-${title}-${id}`}
              ref={ref}
              className="w-full h-full flex flex-col bg-white dark:bg-background overflow-hidden"
            >
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <div className="max-w-6xl mx-auto w-full px-8 md:px-10 py-16 md:py-24">
                  <div className="flex justify-between items-start pb-8 border-b border-neutral-100 dark:border-neutral-800 mb-10">
                    <div className="flex flex-col gap-3 max-w-4xl">
                      <motion.h3
                        layoutId={`title-${title}-${id}`}
                        className="text-3xl md:text-5xl font-bold text-neutral-800 dark:text-foreground tracking-tight"
                      >
                        {title}
                      </motion.h3>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge variant="secondary" className="px-3 py-1 text-sm font-medium bg-primary/10 text-primary border-primary/20">
                          {type}
                        </Badge>
                        <Badge variant="outline" className="px-3 py-1 text-sm font-medium">
                          {location}
                        </Badge>
                        <Badge variant="outline" className="px-3 py-1 text-sm font-medium">
                          {department}
                        </Badge>
                      </div>
                    </div>
                    <motion.a
                      layoutId={`button-${title}-${id}`}
                      href={ctaLink}
                      target="_self"
                      className="px-10 py-4 text-lg rounded-full font-bold bg-primary hover:bg-primary/90 transition-all text-white mt-2 shadow-lg"
                    >
                      {ctaText}
                    </motion.a>
                  </div>

                  <div className="grid lg:grid-cols-3 gap-16 mb-8">
                    <div className="lg:col-span-2 space-y-10">
                      <div>
                        <h4 className="text-xl font-bold text-neutral-800 dark:text-foreground mb-4 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-primary" />
                          Job Description
                        </h4>
                        <motion.p
                          layoutId={`description-${description}-${id}`}
                          className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed"
                        >
                          {description}
                        </motion.p>
                      </div>

                      <div className="space-y-10">
                        <div>
                          <h4 className="text-xl font-bold text-neutral-800 dark:text-foreground mb-4 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            Qualifications
                          </h4>
                          <div className="pl-6 border-l-2 border-neutral-100 dark:border-neutral-800">
                            <p className="whitespace-pre-line text-lg leading-relaxed">{qualifications}</p>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-xl font-bold text-neutral-800 dark:text-foreground mb-4 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            Responsibilities
                          </h4>
                          <div className="pl-6 border-l-2 border-neutral-100 dark:border-neutral-800">
                            <p className="whitespace-pre-line text-lg leading-relaxed">{responsibilities}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-8">
                      <div className="bg-neutral-50 dark:bg-neutral-900/50 p-8 rounded-3xl border border-neutral-100 dark:border-neutral-800 sticky top-0">
                        <h4 className="text-sm uppercase tracking-widest font-bold text-neutral-400 dark:text-neutral-500 mb-6">Quick Details</h4>
                        <div className="space-y-6">
                          <div className="flex flex-col">
                            <span className="text-xs text-neutral-400 mb-1 font-bold uppercase tracking-wider">Stipend / Salary</span>
                            <span className="text-xl font-bold text-neutral-800 dark:text-neutral-200">{salary}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs text-neutral-400 mb-1 font-bold uppercase tracking-wider">Job Type</span>
                            <span className="text-xl font-bold text-neutral-800 dark:text-neutral-200">{type}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs text-neutral-400 mb-1 font-bold uppercase tracking-wider">Location</span>
                            <span className="text-xl font-bold text-neutral-800 dark:text-neutral-200">{location}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs text-neutral-400 mb-1 font-bold uppercase tracking-wider">Working Hours</span>
                            <span className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 leading-snug">{workingHours}</span>
                          </div>
                        </div>

                        <div className="mt-10 pt-8 border-t border-neutral-100 dark:border-neutral-800">
                          <h4 className="text-sm uppercase tracking-widest font-bold text-neutral-400 dark:text-neutral-500 mb-6">Benefits</h4>
                          <div className="pl-6 border-l-2 border-primary/30">
                            <p className="text-neutral-600 dark:text-neutral-400 text-lg leading-relaxed whitespace-pre-line">
                              {benefits}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>
      <motion.div
        className="max-w-6xl p-4 flex flex-col md:flex-row justify-between items-center cursor-pointer border-y border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
        onClick={() => setActive(true)}
        layoutId={`card-${title}-${id}`}
      >
        <div className="flex gap-4 flex-col md:flex-row justify-between w-full">
          <div className="flex flex-col gap-2">
            <motion.h3
              layoutId={`title-${title}-${id}`}
              className="font-medium text-neutral-800 dark:text-neutral-200 text-center md:text-left"
            >
              {title}
            </motion.h3>
            <motion.div
              layoutId={`description-${description}-${id}`}
              className="text-neutral-600 dark:text-neutral-400 text-center md:text-left"
            >
              <Badge variant="outline">{type}</Badge>
            </motion.div>
          </div>
        </div>
        <div className="flex gap-4 flex-col md:flex-row w-full justify-end">
          <div className="flex gap-2">
            <Badge variant="outline">{location}</Badge>
            <Badge variant="outline">{department}</Badge>
          </div>
          <motion.button
            layoutId={`button-${title}-${id}`}
            className="px-4 py-2 text-sm rounded-full font-bold bg-primary hover:bg-primary hover:text-white text-black mt-4 md:mt-0"
          >view more
          </motion.button>
        </div>
      </motion.div>
    </>
  );
}

export const CloseIcon = () => {
  return (
    <motion.svg
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
        transition: {
          duration: 0.05,
        },
      }}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 text-black"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M18 6l-12 12" />
      <path d="M6 6l12 12" />
    </motion.svg>
  );
};

const cards = [
  {
    description: "Lana Del Rey",
    title: "Summertime Sadness",
    src: "https://assets.aceternity.com/demos/lana-del-rey.jpeg",
    ctaText: "Play",
    ctaLink: "https://ui.aceternity.com/templates",
    content: () => {
      return (
        <p>
          Lana Del Rey, an iconic American singer-songwriter, is celebrated for
          her melancholic and cinematic music style. Born Elizabeth Woolridge
          Grant in New York City, she has captivated audiences worldwide with
          her haunting voice and introspective lyrics. <br /> <br /> Her songs
          often explore themes of tragic romance, glamour, and melancholia,
          drawing inspiration from both contemporary and vintage pop culture.
          With a career that has seen numerous critically acclaimed albums, Lana
          Del Rey has established herself as a unique and influential figure in
          the music industry, earning a dedicated fan base and numerous
          accolades.
        </p>
      );
    },
  },
  {
    description: "Babbu Maan",
    title: "Mitran Di Chhatri",
    src: "https://assets.aceternity.com/demos/babbu-maan.jpeg",
    ctaText: "Play",
    ctaLink: "https://ui.aceternity.com/templates",
    content: () => {
      return (
        <p>
          Babu Maan, a legendary Punjabi singer, is renowned for his soulful
          voice and profound lyrics that resonate deeply with his audience. Born
          in the village of Khant Maanpur in Punjab, India, he has become a
          cultural icon in the Punjabi music industry. <br /> <br /> His songs
          often reflect the struggles and triumphs of everyday life, capturing
          the essence of Punjabi culture and traditions. With a career spanning
          over two decades, Babu Maan has released numerous hit albums and
          singles that have garnered him a massive fan following both in India
          and abroad.
        </p>
      );
    },
  },

  {
    description: "Metallica",
    title: "For Whom The Bell Tolls",
    src: "https://assets.aceternity.com/demos/metallica.jpeg",
    ctaText: "Play",
    ctaLink: "https://ui.aceternity.com/templates",
    content: () => {
      return (
        <p>
          Metallica, an iconic American heavy metal band, is renowned for their
          powerful sound and intense performances that resonate deeply with
          their audience. Formed in Los Angeles, California, they have become a
          cultural icon in the heavy metal music industry. <br /> <br /> Their
          songs often reflect themes of aggression, social issues, and personal
          struggles, capturing the essence of the heavy metal genre. With a
          career spanning over four decades, Metallica has released numerous hit
          albums and singles that have garnered them a massive fan following
          both in the United States and abroad.
        </p>
      );
    },
  },
  {
    description: "Led Zeppelin",
    title: "Stairway To Heaven",
    src: "https://assets.aceternity.com/demos/led-zeppelin.jpeg",
    ctaText: "Play",
    ctaLink: "https://ui.aceternity.com/templates",
    content: () => {
      return (
        <p>
          Led Zeppelin, a legendary British rock band, is renowned for their
          innovative sound and profound impact on the music industry. Formed in
          London in 1968, they have become a cultural icon in the rock music
          world. <br /> <br /> Their songs often reflect a blend of blues, hard
          rock, and folk music, capturing the essence of the 1970s rock era.
          With a career spanning over a decade, Led Zeppelin has released
          numerous hit albums and singles that have garnered them a massive fan
          following both in the United Kingdom and abroad.
        </p>
      );
    },
  },
  {
    description: "Mustafa Zahid",
    title: "Toh Phir Aao",
    src: "https://assets.aceternity.com/demos/toh-phir-aao.jpeg",
    ctaText: "Play",
    ctaLink: "https://ui.aceternity.com/templates",
    content: () => {
      return (
        <p>
          &quot;Aawarapan&quot;, a Bollywood movie starring Emraan Hashmi, is
          renowned for its intense storyline and powerful performances. Directed
          by Mohit Suri, the film has become a significant work in the Indian
          film industry. <br /> <br /> The movie explores themes of love,
          redemption, and sacrifice, capturing the essence of human emotions and
          relationships. With a gripping narrative and memorable music,
          &quot;Aawarapan&quot; has garnered a massive fan following both in
          India and abroad, solidifying Emraan Hashmi&apos;s status as a
          versatile actor.
        </p>
      );
    },
  },
];
